import { AxiosResponse } from 'axios'
import { QueryClient, queryOptions } from '@tanstack/react-query'
import type {
  Feature,
  FeatureFlagRow,
  FeatureFlagsResponse,
  FeaturesResponse,
} from '../generated/api/features'
import axiosAuth from './config'
import { createUrl } from './helpers'

const KNOWN_FEATURES: Record<Feature, true> = { ticket: true }

export const isFeature = (value: string): value is Feature =>
  Object.hasOwn(KNOWN_FEATURES, value)

const getFeatures = async (): Promise<
  AxiosResponse<{ data: FeaturesResponse }>
> => {
  return axiosAuth.get<{ data: FeaturesResponse }>(createUrl(`/features`))
}

const getFeatureFlags = (): Promise<
  AxiosResponse<{ data: FeatureFlagsResponse }>
> => {
  return axiosAuth.get<{ data: FeatureFlagsResponse }>(
    createUrl('/admin/features')
  )
}

const featureFlagPath = (
  feature: Feature,
  userId?: string,
  societyId?: string
): string => {
  if (userId != null) {
    return `/admin/features/${feature}/users/${userId}`
  }
  return societyId == null
    ? `/admin/features/${feature}/global`
    : `/admin/features/${feature}/societies/${societyId}`
}

/** Turns a flag on or off for the user or society named, or for everyone when neither is. */
export const switchFeatureFlag = ({
  feature,
  userId,
  societyId,
  on,
}: {
  feature: Feature
  userId?: string
  societyId?: string
  on: boolean
}): Promise<AxiosResponse<{ data: FeatureFlagRow }>> => {
  const url = createUrl(featureFlagPath(feature, userId, societyId))
  return on
    ? axiosAuth.put<{ data: FeatureFlagRow }>(url)
    : axiosAuth.delete<{ data: FeatureFlagRow }>(url)
}

export const featureQueries = {
  getFeatures: () =>
    queryOptions({
      queryKey: [{ scope: 'features', entity: 'getFeatures' }],
      queryFn: async () => {
        return (await getFeatures()).data.data
      },
    }),
  featureFlagList: () =>
    queryOptions({
      queryKey: [{ scope: 'featureFlags', entity: 'list' }] as const,
      queryFn: async () => (await getFeatureFlags()).data.data,
    }),
}

export const applyFeatureFlagRow = (
  client: QueryClient,
  row: FeatureFlagRow
): void => {
  client.setQueryData(featureQueries.featureFlagList().queryKey, (current) =>
    current == null
      ? current
      : {
          flags: current.flags.map((flag) =>
            flag.feature === row.feature ? row : flag
          ),
        }
  )
}
